import type { ChangeEvent, DragEvent, InputHTMLAttributes, RefObject } from 'react';
import { useRef } from 'react';

// Fallback MIME lookup for browsers that leave `File.type` empty
// (common on some mobile browsers where the OS file picker doesn't
// populate a MIME type). Lets us validate a picked file by extension
// when MIME alone isn't reliable.
const TYPE_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  heic: 'image/heic',
  heif: 'image/heif',
  avif: 'image/avif',
  tiff: 'image/tiff',
  tif: 'image/tiff',
};

const getEffectiveType = (file: File): string => {
  if (file.type) {
    return file.type;
  }
  const dot = file.name.lastIndexOf('.');
  if (dot < 0) {
    return '';
  }
  const ext = file.name.slice(dot + 1).toLowerCase();
  return TYPE_BY_EXTENSION[ext] ?? '';
};

const isTypeAllowed = (type: string, acceptedFileTypes?: readonly string[]) => {
  if (acceptedFileTypes && acceptedFileTypes.length > 0) {
    return acceptedFileTypes.includes(type);
  }
  return type.startsWith('image/');
};

interface Args {
  acceptedFileTypes?: readonly string[];
  maxFileSizeBytes?: number;
  /** Max number of files the customer may add at once. When omitted, no
   * cap is enforced. When set, the file input's `multiple` attribute is
   * disabled if only a single slot remains, so the OS picker shows a
   * single-file UI rather than a multi-select. */
  maxFiles?: number;
  onFilesRejected?: (rejected: { oversized: File[]; wrongType: File[] }) => void;
  onFilesSelected: (files: File[]) => void;
}

interface Result {
  dropzoneProps: {
    onClick: () => void;
    onDragOver: (e: DragEvent<HTMLElement>) => void;
    onDrop: (e: DragEvent<HTMLElement>) => void;
  };
  inputProps: InputHTMLAttributes<HTMLInputElement> & { ref: RefObject<HTMLInputElement> };
}

/**
 * Shared file-input + drag/drop + type/size validation behavior used by
 * `PhotoPickerDropzone` (inside the drawer) and `PhotoPickerTrigger`'s empty
 * state (on the main page). Returns ready-to-spread props for the outer
 * dropzone element and the hidden file input.
 */
export function usePhotoFileInput({
  acceptedFileTypes,
  maxFileSizeBytes,
  maxFiles,
  onFilesRejected,
  onFilesSelected,
}: Args): Result {
  const inputRef = useRef<HTMLInputElement>(null);

  // When every accepted type is an image, widen the picker to `image/*` so the
  // user can pick (and learn from a validation error about) non-allowlisted
  // images such as HEIC instead of being silently blocked by the OS picker.
  // For mixed-MIME allowlists we fall back to a strict join.
  const acceptAttr =
    !acceptedFileTypes?.length || acceptedFileTypes.every((t) => t.startsWith('image/'))
      ? 'image/*'
      : acceptedFileTypes.join(',');

  const isOversized = (file: File) => maxFileSizeBytes != null && file.size > maxFileSizeBytes;

  const partition = (files: File[]) => {
    const accepted: File[] = [];
    const oversized: File[] = [];
    const wrongType: File[] = [];
    for (const file of files) {
      const typeOk = isTypeAllowed(getEffectiveType(file), acceptedFileTypes);
      const sizeOk = !isOversized(file);
      if (typeOk && sizeOk) {
        accepted.push(file);
        continue;
      }
      // A file that fails both checks lands in both buckets so the user sees
      // every reason it was rejected, not just the first one we noticed.
      if (!typeOk) {
        wrongType.push(file);
      }
      if (!sizeOk) {
        oversized.push(file);
      }
    }
    // Trim the accepted list to the remaining capacity so callers don't have
    // to re-implement the same cap themselves. Excess files are dropped silently.
    return {
      accepted: maxFiles != null ? accepted.slice(0, maxFiles) : accepted,
      oversized,
      wrongType,
    };
  };

  const emit = (files: File[]) => {
    const { accepted, oversized, wrongType } = partition(files);
    if (accepted.length > 0) {
      onFilesSelected(accepted);
    }
    onFilesRejected?.({ oversized, wrongType });
  };

  const openPicker = () => inputRef.current?.click();

  return {
    dropzoneProps: {
      onClick: openPicker,
      onDragOver: (e) => {
        e.preventDefault();
        // `dataTransfer.items` only exposes `.type` during dragover (no size),
        // so the cursor hint reflects type only — size is still enforced on drop.
        const fileItems = Array.from(e.dataTransfer.items).filter((item) => item.kind === 'file');
        const hasInvalid = fileItems.some((item) => !isTypeAllowed(item.type, acceptedFileTypes));
        e.dataTransfer.dropEffect = hasInvalid ? 'none' : 'copy';
      },
      onDrop: (e) => {
        e.preventDefault();
        emit(Array.from(e.dataTransfer.files));
      },
    },
    inputProps: {
      accept: acceptAttr,
      hidden: true,
      multiple: maxFiles == null || maxFiles > 1,
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        emit(Array.from(e.target.files ?? []));
        e.target.value = '';
      },
      ref: inputRef,
      type: 'file',
    },
  };
}
